import Papa from "papaparse";

import { validateAndSanitizeOutcomes } from "../../app/audit/simple/utils/outcomesDataIntegrity";

const FIXED_FOLLOW_UP_RULE_NAMES = {
  max: ["Highest Score", "Second Highest Score", "Third Highest Score"],
  min: ["Lowest Score", "Second Lowest Score", "Third Lowest Score"],
};

const FOLLOW_UP_FIELD_DEFINITIONS = [
  { key: "feedbackText", suffix: "Initial User Answer" },
  { key: "followUpQ", suffix: "AI Question Text" },
  { key: "followUpA", suffix: "User Answer to AI" },
  { key: "questionText", suffix: "Source Question Text" },
];

const FOLLOW_UP_RULE_TYPE_ORDER = {
  generic: 0,
  max: 1,
  min: 2,
  other: 3,
};

const isPromptFollowUpItem = (baseItem, candidate) =>
  Boolean(
    baseItem &&
      candidate &&
      candidate.dimension == baseItem.dimension &&
      candidate.factor == baseItem.factor &&
      (candidate.prompt_question || candidate.prompt_feedback)
  );

const formatOrdinal = (value) => {
  const mod100 = value % 100;

  if (mod100 >= 11 && mod100 <= 13) {
    return `${value}th`;
  }

  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
};

const getRuleNameForRank = (type, index) => {
  const fixedRuleName = FIXED_FOLLOW_UP_RULE_NAMES[type]?.[index];
  if (fixedRuleName) {
    return fixedRuleName;
  }

  const rank = formatOrdinal(index + 1);
  if (type === "max") {
    return `${rank} Highest Score`;
  }

  if (type === "min") {
    return `${rank} Lowest Score`;
  }

  return `Follow Up ${index + 1}`;
};

const getComparableResponse = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const addFollowUpColumns = (columns, ruleName, followUp) => {
  FOLLOW_UP_FIELD_DEFINITIONS.forEach(({ key, suffix }) => {
    columns[`${ruleName} ${suffix}`] = followUp[key] ?? "";
  });
};

const isPresent = (value) => value !== null && value !== undefined && value !== "";

const toKey = (value) => String(value);

const getUniqueRefs = (values) =>
  Array.from(
    new Set(values.filter((value) => isPresent(value)).map((value) => toKey(value)))
  );

const normalizeQuestionStructure = (questionSource) => {
  if (!questionSource) {
    return null;
  }

  if (
    Array.isArray(questionSource?.dimensions) ||
    Array.isArray(questionSource?.comments)
  ) {
    return questionSource;
  }

  if (questionSource?.questions) {
    return {
      title: questionSource.questions.title,
      dimensions: questionSource.questions.dimensions,
      type: questionSource.questions.type,
      type_name: questionSource.questions.type_name,
      comments: questionSource.questions.comments,
      useLikert: questionSource.questions.useLikert,
      analytics: questionSource.questions.analytics,
    };
  }

  return null;
};

const normalizeOutcomeQuestionStructure = (outcomeSource) => {
  if (!outcomeSource || !Array.isArray(outcomeSource?.questions)) {
    return null;
  }

  return validateAndSanitizeOutcomes(outcomeSource).sanitized;
};

const getSurveySortOrder = (surveyEntry) =>
  surveyEntry?.datasetMetadata?.selectedSortOrder ??
  surveyEntry?.question?.sort_order ??
  surveyEntry?.question_sort_order ??
  surveyEntry?.sort_order ??
  null;

const getOutcomeSortOrder = (
  surveyEntry,
  organizationData,
  fallbackOutcomeQuestions
) =>
  surveyEntry?.datasetMetadata?.outcomeQuestions?.sort_order ??
  surveyEntry?.additional_questions?.outcome_question?.question?.sort_order ??
  fallbackOutcomeQuestions?.sort_order ??
  organizationData?.styling?.survey_sequence?.find(
    (item) => item?.value === "outcome_question"
  )?.question?.sort_order ??
  null;

export const normalizeCsvCell = (value, options = {}) => {
  const { collapseNewlines = true } = options;

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value !== "string") {
    return value;
  }

  return collapseNewlines ? value.replace(/\r\n|\n|\r/g, " ") : value;
};

export const normalizeCsvHeader = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value !== "string") {
    return String(value);
  }

  return value
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u00A0/g, " ")
    .trim();
};

export const normalizeFollowUpType = (type) => {
  if (typeof type !== "string") {
    return null;
  }

  const normalizedType = type.trim().toLowerCase();

  if (
    normalizedType === "max" ||
    normalizedType.includes("high") ||
    normalizedType.includes("max")
  ) {
    return "max";
  }

  if (
    normalizedType === "min" ||
    normalizedType.includes("low") ||
    normalizedType.includes("min")
  ) {
    return "min";
  }

  return null;
};

const normalizeTextValue = (value) => normalizeCsvCell(value || "");

const getFeedbackTextValue = (item) =>
  normalizeTextValue(
    item?.feedback ??
      item?.comment ??
      (typeof item?.response === "string" ? item.response : "")
  );

const getPromptSourceFeedback = (baseItem, promptItem) =>
  normalizeTextValue(promptItem?.prompt_feedback || getFeedbackTextValue(baseItem));

const getPromptFollowUpAnswer = (promptItem) =>
  normalizeTextValue(
    promptItem?.feedback ??
      promptItem?.follow_up ??
      (typeof promptItem?.response === "string" ? promptItem.response : "")
  );

const parseFollowUpColumnKey = (key) => {
  if (typeof key !== "string") {
    return null;
  }

  const fieldDefinition = FOLLOW_UP_FIELD_DEFINITIONS.find(({ suffix }) =>
    key.endsWith(` ${suffix}`)
  );

  if (!fieldDefinition) {
    return null;
  }

  const ruleName = key.slice(0, key.length - fieldDefinition.suffix.length - 1);
  const genericMatch = ruleName.match(/^Follow Up (\d+)$/);
  if (genericMatch) {
    return {
      type: "generic",
      rank: Number(genericMatch[1]),
      fieldIndex: FOLLOW_UP_FIELD_DEFINITIONS.indexOf(fieldDefinition),
    };
  }

  const maxFixedIndex = FIXED_FOLLOW_UP_RULE_NAMES.max.indexOf(ruleName);
  if (maxFixedIndex !== -1) {
    return {
      type: "max",
      rank: maxFixedIndex + 1,
      fieldIndex: FOLLOW_UP_FIELD_DEFINITIONS.indexOf(fieldDefinition),
    };
  }

  const minFixedIndex = FIXED_FOLLOW_UP_RULE_NAMES.min.indexOf(ruleName);
  if (minFixedIndex !== -1) {
    return {
      type: "min",
      rank: minFixedIndex + 1,
      fieldIndex: FOLLOW_UP_FIELD_DEFINITIONS.indexOf(fieldDefinition),
    };
  }

  const dynamicMatch = ruleName.match(/^(\d+)(st|nd|rd|th) (Highest|Lowest) Score$/);
  if (dynamicMatch) {
    return {
      type: dynamicMatch[3] === "Highest" ? "max" : "min",
      rank: Number(dynamicMatch[1]),
      fieldIndex: FOLLOW_UP_FIELD_DEFINITIONS.indexOf(fieldDefinition),
    };
  }

  return {
    type: "other",
    rank: Number.POSITIVE_INFINITY,
    fieldIndex: FOLLOW_UP_FIELD_DEFINITIONS.indexOf(fieldDefinition),
  };
};

const sortFollowUpColumnKeys = (keys) =>
  [...keys].sort((a, b) => {
    const parsedA = parseFollowUpColumnKey(a);
    const parsedB = parseFollowUpColumnKey(b);

    if (!parsedA || !parsedB) {
      return String(a).localeCompare(String(b));
    }

    return (
      FOLLOW_UP_RULE_TYPE_ORDER[parsedA.type] -
        FOLLOW_UP_RULE_TYPE_ORDER[parsedB.type] ||
      parsedA.rank - parsedB.rank ||
      parsedA.fieldIndex - parsedB.fieldIndex ||
      a.localeCompare(b)
    );
  });

export const extractFollowUps = (feedbackArray) => {
  if (!Array.isArray(feedbackArray)) {
    return [];
  }

  const followUps = [];

  feedbackArray.forEach((fbItem, idx) => {
    const prevItem = feedbackArray[idx - 1];
    if (isPromptFollowUpItem(prevItem, fbItem)) {
      return;
    }

    const nextItem = feedbackArray[idx + 1];
    const hasConsecutiveFollowUp = isPromptFollowUpItem(fbItem, nextItem);
    const hasDirectFollowUp =
      typeof fbItem?.follow_up_question === "string" &&
      fbItem.follow_up_question.trim().length > 0;

    if (!hasDirectFollowUp && !hasConsecutiveFollowUp) {
      return;
    }

    const aiFollowUpQ = hasDirectFollowUp
      ? fbItem.follow_up_question
      : nextItem?.prompt_question || nextItem?.follow_up_question || "";
    const aiFollowUpA = hasDirectFollowUp
      ? fbItem.follow_up || ""
      : getPromptFollowUpAnswer(nextItem);
    const sourceFeedbackText = hasConsecutiveFollowUp
      ? getPromptSourceFeedback(fbItem, nextItem)
      : getFeedbackTextValue(fbItem);

    followUps.push({
      type: normalizeFollowUpType(fbItem?.type),
      response: fbItem?.response,
      originalIndex: idx,
      questionText: normalizeTextValue(fbItem?.question),
      feedbackText: sourceFeedbackText,
      followUpQ: normalizeTextValue(aiFollowUpQ),
      followUpA: normalizeTextValue(aiFollowUpA),
    });
  });

  return followUps;
};

export const followUpsToColumns = (followUpsArray) => {
  if (!Array.isArray(followUpsArray) || followUpsArray.length === 0) {
    return {};
  }

  const columns = {};

  const rankedMaxFollowUps = followUpsArray
    .filter((followUp) => followUp.type === "max")
    .sort(
      (a, b) =>
        getComparableResponse(b.response, Number.NEGATIVE_INFINITY) -
          getComparableResponse(a.response, Number.NEGATIVE_INFINITY) ||
        a.originalIndex - b.originalIndex
    );

  const rankedMinFollowUps = followUpsArray
    .filter((followUp) => followUp.type === "min")
    .sort(
      (a, b) =>
        getComparableResponse(a.response, Number.POSITIVE_INFINITY) -
          getComparableResponse(b.response, Number.POSITIVE_INFINITY) ||
        a.originalIndex - b.originalIndex
    );

  const untypedFollowUps = followUpsArray
    .filter((followUp) => followUp.type !== "max" && followUp.type !== "min")
    .sort((a, b) => a.originalIndex - b.originalIndex);

  rankedMaxFollowUps.forEach((followUp, index) => {
    addFollowUpColumns(columns, getRuleNameForRank("max", index), followUp);
  });

  rankedMinFollowUps.forEach((followUp, index) => {
    addFollowUpColumns(columns, getRuleNameForRank("min", index), followUp);
  });

  untypedFollowUps.forEach((followUp, index) => {
    addFollowUpColumns(columns, getRuleNameForRank(null, index), followUp);
  });

  return columns;
};

export const resolveExportDefinitions = ({
  surveyEntry,
  surveyDefinitions,
  outcomeDefinitions,
  organizationData,
  fallbackQuestionStructure,
  fallbackOutcomeQuestions,
}) => {
  const surveySortOrder = getSurveySortOrder(surveyEntry);
  const outcomeSortOrder = getOutcomeSortOrder(
    surveyEntry,
    organizationData,
    fallbackOutcomeQuestions
  );

  const matchedSurveyDefinition = Array.isArray(surveyDefinitions)
    ? surveyDefinitions.find(
        (definition) => Number(definition?.sort_order) === Number(surveySortOrder)
      )
    : null;
  const matchedOutcomeDefinition = Array.isArray(outcomeDefinitions)
    ? outcomeDefinitions.find(
        (definition) => Number(definition?.sort_order) === Number(outcomeSortOrder)
      )
    : null;
  const embeddedOutcomeQuestions = normalizeOutcomeQuestionStructure(
    surveyEntry?.datasetMetadata?.outcomeQuestions
  );

  return {
    surveySortOrder,
    outcomeSortOrder,
    questionStructure:
      normalizeQuestionStructure(matchedSurveyDefinition) ||
      normalizeQuestionStructure(fallbackQuestionStructure),
    outcomeQuestions:
      embeddedOutcomeQuestions ||
      normalizeOutcomeQuestionStructure(matchedOutcomeDefinition) ||
      normalizeOutcomeQuestionStructure(fallbackOutcomeQuestions),
  };
};

const normalizeLookupRef = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : String(value);
};

const buildCompositeQuestionKey = (dimension, factor, question) =>
  `${normalizeLookupRef(dimension)}:${normalizeLookupRef(
    factor
  )}:${normalizeLookupRef(question)}`;

const buildCompositeOutcomeKey = (question, subQuestion) =>
  `${normalizeLookupRef(question)}:${normalizeLookupRef(subQuestion)}`;

export const flattenSurveyQuestions = (questionStructure) => {
  const flattenedQuestions = [];

  (questionStructure?.dimensions || []).forEach((dimension, dimensionIndex) => {
    (dimension?.factors || []).forEach((factor, factorIndex) => {
      (factor?.questions || []).forEach((question, questionIndex) => {
        flattenedQuestions.push({
          dimension: dimensionIndex,
          factor: factorIndex,
          q: questionIndex,
          header: normalizeCsvHeader(
            question?.q || question?.question || question?.title || ""
          ),
        });
      });
    });
  });

  return flattenedQuestions;
};

export const buildResponseQuestionLookup = (questionResponses) => {
  const lookup = {};

  (questionResponses || []).forEach((questionResponse) => {
    lookup[
      buildCompositeQuestionKey(
        questionResponse?.id,
        questionResponse?.factor,
        questionResponse?.q
      )
    ] = questionResponse?.response ?? "";
  });

  return lookup;
};

export const findQuestionResponseValue = (
  questionLookup,
  surveyQuestionDefinition
) => {
  if (!questionLookup || !surveyQuestionDefinition) {
    return "";
  }

  return (
    questionLookup[
      buildCompositeQuestionKey(
        surveyQuestionDefinition.dimension,
        surveyQuestionDefinition.factor,
        surveyQuestionDefinition.q
      )
    ] ?? ""
  );
};

export const buildQuestionLookup = (questionStructure) => {
  const lookup = {
    byOrder: {},
    byCompositeKey: {},
    comments: Array.isArray(questionStructure?.comments)
      ? questionStructure.comments
      : [],
  };

  if (!Array.isArray(questionStructure?.dimensions)) {
    return lookup;
  }

  let overallOrder = 1;

  questionStructure.dimensions.forEach((dimension, dimensionIndex) => {
    const dimensionRefs = getUniqueRefs([
      dimension?.id,
      dimensionIndex,
      dimensionIndex + 1,
    ]);

    (dimension?.factors || []).forEach((factor, factorIndex) => {
      const factorRefs = getUniqueRefs([
        factor?.id,
        factorIndex,
        factorIndex + 1,
      ]);

      (factor?.questions || []).forEach((question, questionIndex) => {
        const header = normalizeCsvHeader(
          question?.q || question?.question || question?.title || ""
        );

        if (!header) {
          overallOrder += 1;
          return;
        }

        lookup.byOrder[overallOrder] = header;

        const questionRefs = getUniqueRefs([
          question?.id,
          question?.q_id,
          questionIndex,
          questionIndex + 1,
        ]);

        dimensionRefs.forEach((dimensionRef) => {
          factorRefs.forEach((factorRef) => {
            questionRefs.forEach((questionRef) => {
              lookup.byCompositeKey[
                `${dimensionRef}:${factorRef}:${questionRef}`
              ] = header;
            });
          });
        });

        overallOrder += 1;
      });
    });
  });

  return lookup;
};

export const findQuestionHeader = (questionLookup, responseQuestion) => {
  if (!questionLookup || !responseQuestion) {
    return "";
  }

  const orderRefs = getUniqueRefs([
    responseQuestion?.ques_order,
    responseQuestion?.order,
    responseQuestion?.quesOrder,
  ]);

  for (const orderRef of orderRefs) {
    const header = questionLookup.byOrder[Number(orderRef)];
    if (header) {
      return header;
    }
  }

  const dimensionRefs = getUniqueRefs([
    responseQuestion?.id,
    responseQuestion?.dimension,
    responseQuestion?.d,
  ]);
  const factorRefs = getUniqueRefs([
    responseQuestion?.factor,
    responseQuestion?.section,
    responseQuestion?.f,
  ]);
  const questionRefs = getUniqueRefs([
    responseQuestion?.q,
    responseQuestion?.question,
    responseQuestion?.question_id,
    responseQuestion?.qid,
  ]);

  for (const dimensionRef of dimensionRefs) {
    for (const factorRef of factorRefs) {
      for (const questionRef of questionRefs) {
        const header =
          questionLookup.byCompositeKey[
            `${dimensionRef}:${factorRef}:${questionRef}`
          ];
        if (header) {
          return header;
        }
      }
    }
  }

  return "";
};

export const flattenOutcomeQuestions = (outcomeQuestions) => {
  const flattenedQuestions = [];

  (
    normalizeOutcomeQuestionStructure(outcomeQuestions)?.questions || []
  ).forEach((section) => {
    (section?.questions || []).forEach((question) => {
      const outcomeId = isPresent(question?.q) ? question.q : section?.id;
      const sectionId = isPresent(question?.s) ? question.s : question?.id;

      flattenedQuestions.push({
        header: normalizeCsvHeader(
          question?.question || question?.name || question?.q || question?.title || ""
        ),
        outcomeId: isPresent(outcomeId) ? normalizeLookupRef(outcomeId) : null,
        sectionId: isPresent(sectionId) ? normalizeLookupRef(sectionId) : null,
      });
    });
  });

  return flattenedQuestions;
};

export const buildOutcomeResponseLookup = (outcomeResponses) => {
  const lookup = {};

  (outcomeResponses || []).forEach((outcomeResponse) => {
    if (!(outcomeResponse?.clicked === true || outcomeResponse?.clicked === undefined)) {
      return;
    }

    if (!isPresent(outcomeResponse?.q) || !isPresent(outcomeResponse?.s)) {
      return;
    }

    lookup[
      buildCompositeOutcomeKey(outcomeResponse.q, outcomeResponse.s)
    ] = outcomeResponse?.response ?? "";
  });

  return lookup;
};

export const findOutcomeResponseValue = (responses, outcomeQuestion) => {
  if (!outcomeQuestion) {
    return "";
  }

  if (!isPresent(outcomeQuestion?.outcomeId) || !isPresent(outcomeQuestion?.sectionId)) {
    return "";
  }

  const lookupKey = buildCompositeOutcomeKey(
    outcomeQuestion.outcomeId,
    outcomeQuestion.sectionId
  );

  if (Array.isArray(responses)) {
    const matchedResponse = responses.find(
      (response) =>
        (response?.clicked === true || response?.clicked === undefined) &&
        isPresent(response?.q) &&
        isPresent(response?.s) &&
        buildCompositeOutcomeKey(response.q, response.s) === lookupKey
    );

    return matchedResponse?.response ?? "";
  }

  return responses?.[lookupKey] ?? "";
};

const normalizeFilenamePart = (value) => {
  if (value === null || value === undefined || value === "") {
    return "unknown";
  }

  return String(value)
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "unknown";
};

export const buildExportFilename = ({
  baseName = "raw_scores",
  surveySortOrder,
  outcomeSortOrder,
  suffix,
}) => {
  const parts = [
    normalizeFilenamePart(baseName),
    `survey-sort-order-${normalizeFilenamePart(surveySortOrder)}`,
    `outcome-sort-order-${normalizeFilenamePart(outcomeSortOrder)}`,
  ];

  if (suffix) {
    parts.push(normalizeFilenamePart(suffix));
  }

  return `${parts.join("_")}.csv`;
};

export const buildHeaderKeys = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const allKeys = new Set();

  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => allKeys.add(key));
  });

  const orderedKeys = Array.from(allKeys);
  const nonFollowUpKeys = orderedKeys.filter((key) => !parseFollowUpColumnKey(key));
  const followUpKeys = orderedKeys.filter((key) => parseFollowUpColumnKey(key));

  return [...nonFollowUpKeys, ...sortFollowUpColumnKeys(followUpKeys)];
};

export const buildCsvContent = (rows, headerKeys) => {
  const fields = Array.isArray(headerKeys) ? headerKeys : buildHeaderKeys(rows);

  const csv = Papa.unparse({
    fields,
    data: (rows || []).map((row) =>
      fields.map((field) => normalizeCsvCell(row?.[field]))
    ),
  });

  return `\uFEFF${csv}`;
};
